import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTemplateById } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import SuccessAlert from '../UI/Alert/SuccessAlert';
import PreviewTemplate from '../GlobalTemplate/PreviewTemplate';
import { editTemplates } from '../../utils/APIs';
import Loader from '../UI/Loader/Loader';
import { toast } from 'react-toastify';
import backwardArrow from '../../assets/icons/backward-arrow.svg';
import { useContentfulContext } from '../../contexts/ContentfulContext';

const EditGlobalTemplate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [template, setTemplate] = useState({});
    const [loading, setLoading] = useState(false);
    const { userData } = useUserContext();
    const { enums, content } = useContentfulContext();

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [cssCode, setCssCode] = useState("");
    const [htmlCode, setHtmlCode] = useState("");
    const [jsonSchemaCode, setJsonSchemaCode] = useState("");
    const [configJsonCode, setConfigJsonCode] = useState("");
    const [templateName, setTemplateName] = useState('');
    const [templateIconURL, setTemplateIconURL] = useState({ web_only: "", mobile_only: "" });
    const [jsonBSchemaCode, setJsonBSchemaCode] = useState("");
    const [templateType, setTemplateType] = useState("");
    const [templatePlatform, setTemplatePlatform] = useState("");
    const [isLandscapeSupport, setIsLandscapeSupport] = useState(true);
    const [templateSource, setTemplateSource] = useState("");
    const [apiEndPoint, setApiEndPoint] = useState("");

    const getTemplateByIdHandler = async (id) => {
        try {
            setLoading(true);
            const result = await getTemplateById(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                id,
                userData?.defaultAEMAdobeIO?._id
            );
    
            setTemplate(result.data);
            setErrorMessage('');
        } catch (error) {
            toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
            navigator("/admin/global-template/manage-global-templates")
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getTemplateByIdHandler(id);
    }, []);

    useEffect(() => {
        if (Object.keys(template).length > 0) {
            setCssCode(template?.css || "");
            setHtmlCode(template?.html || "");
            setConfigJsonCode(template?.config_json || "");
            setJsonSchemaCode(template?.json_schema || "");
            setTemplateName(template?.name || "");
            setTemplateIconURL({ web_only: template?.icon_url?.web_only, mobile_only: template?.icon_url?.mobile_only });
            setTemplateType(template?.type || "");
            setTemplatePlatform(template?.platform)
            setIsLandscapeSupport(template?.landscape_support)
            setTemplateSource(template?.source || "");
            setApiEndPoint(template?.api_end_point || "");
        }
    }, [template]);

    const validateTemplateName = () => {
        if (!templateName.trim()) {
            setErrorMessage("Template name is required");
            return false;
        }
        setErrorMessage("");
        return true;
    };

    const validateURL = () => {
        const trimmedURL = apiEndPoint.trim();
        try {
            new URL(trimmedURL);
            setErrorMessage("");
            return true;
        } catch (e) {
            setErrorMessage("Invalid URL");
            return false;
        }
    }

    const editTemplateHandler = async () => {
        try {
            setLoading(true);
            const payload = {
                name: templateName,
                icon_url: templateIconURL,
                html: htmlCode,
                css: cssCode,
                type: templateType,
                platform: templatePlatform,
                landscape_support: isLandscapeSupport,
                source: templateSource,
                api_end_point: apiEndPoint,
                config_json: configJsonCode,
                json_schema: templateSource === enums?.templateSources.api.id ? "{}" : jsonSchemaCode,
            };
            const result = await editTemplates(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                id,
                payload,
                "global"
            );
            setTemplate(result.data);
            setErrorMessage("");
            setSuccessMessage("Global Template updated successfully");
        } catch (error) {
            setErrorMessage(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    };

    const saveTemplateHandler = (lev) => {
        const isTemplateNameValid = validateTemplateName();
        const isUrlValid = templateSource === "api" ? validateURL() : true;
    
        if (isTemplateNameValid && isUrlValid) {
            editTemplateHandler(lev);
        }
    };

    return (
        <div>
            {loading && <Loader />}
            {successMessage && (
                <SuccessAlert disappearTime={5000}>{successMessage}</SuccessAlert>
            )}
            <div class="my-4 px-8 w-full">
                <a href="#" class="font-medium text-primary hover:underline flex items-center" onClick={(e) => {
                    e.preventDefault();
                    navigate("/admin/global-template/manage-global-templates")
                }}>
                    <img src={backwardArrow} alt='' className='w-5 h-5 mr-2' /> {content.MANAGE_GLOBAL_TEMPLATE_CONTENT.back_cta_text}
                </a>
            </div>
            { Object.keys(template).length > 0 && <div class="flex flex-col w-full px-4 md:px-8"> 
                <PreviewTemplate
                    templateName={templateName}
                    setTemplateName={setTemplateName}
                    templateIconURL={templateIconURL}
                    setTemplateIconURL={setTemplateIconURL}
                    cssCode={cssCode}
                    htmlCode={htmlCode}
                    setHtmlCode={setHtmlCode}
                    setCssCode={setCssCode}
                    configJsonCode={configJsonCode}
                    setConfigJsonCode={setConfigJsonCode}
                    selectedTemplate={template}
                    validateTemplateName={validateTemplateName}
                    submitError={errorMessage}
                    saveTemplateHandler={saveTemplateHandler}
                    selectedTemplateId={id}
                    jsonSchemaCode={jsonSchemaCode}
                    setJsonSchemaCode={setJsonSchemaCode}
                    jsonBSchemaCode={jsonBSchemaCode}
                    setJsonBSchemaCode={setJsonBSchemaCode}
                    templateType={templateType}
                    setTemplateType={setTemplateType}
                    templatePlatform={templatePlatform}
                    setTemplatePlatform={setTemplatePlatform}
                    isLandscapeSupport={isLandscapeSupport}
                    setIsLandscapeSupport={setIsLandscapeSupport}
                    templateSource={templateSource}
                    setTemplateSource={setTemplateSource}
                    apiEndPoint={apiEndPoint}
                    setApiEndPoint={setApiEndPoint}
                    validateURL={validateURL}
                />
            </div> }
        </div>
    )
}

export default EditGlobalTemplate;